import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import {
  Container,
  Typography,
  Grid,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Avatar,
  Paper,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import LazyMount from '../components/LazyMount';
import PictureImage from '../components/PictureImage';
import { useSEOMeta, BreadcrumbSchema, HomePageSchema } from '../components/SEO';
import { SEO_PAGES, HOME_OG } from '../config/seo';
import ArticleImage from '../components/ArticleImage';
import { HOME_FEATURED_ARTICLE_SLUGS, HOME_FAQ_ITEMS } from '../config/homeContent';
import { getExperienceYears, getHappyPatientCount } from '../utils/dateUtils';
import { articleAPI } from '../services/api';
import { Article } from '../types';
import useStaticPage from '../hooks/useStaticPage';
import { cachedRequest, deferNonCriticalTask } from '../utils/requestCache';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ArticleIcon from '@mui/icons-material/Article';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import BusinessIcon from '@mui/icons-material/Business';
import FaceIcon from '@mui/icons-material/Face';
import StarIcon from '@mui/icons-material/Star';
import PhoneIcon from '@mui/icons-material/Phone';
import { trackPhoneClick } from '../utils/analytics';

const Carousel = lazy(() => import('../components/Carousel'));
const GoogleReviews = lazy(() => import('../components/GoogleReviews'));
const NewsletterSubscribe = lazy(() => import('../components/NewsletterSubscribe'));
const GoogleMapEmbed = lazy(() => import('../components/GoogleMapEmbed'));
const HomeFaqSection = lazy(() => import('../components/HomeFaqSection'));
const WhyChooseUsSection = lazy(() => import('../components/WhyChooseUsSection'));

const BELOW_FOLD_SX = { contentVisibility: 'auto', containIntrinsicSize: '0 500px' } as const;

const SECTION_SOFT_SX = {
  py: { xs: 5, md: 6 },
  px: { xs: 2, md: 3 },
  borderRadius: 4,
  background: 'linear-gradient(160deg, #f7fffe 0%, #eef9f7 55%, #f5fbfa 100%)',
  border: '1px solid rgba(53,188,179,0.12)',
} as const;

const SECTION_TITLE_ACCENT_SX = {
  width: 48,
  height: 4,
  borderRadius: 999,
  background: 'linear-gradient(90deg, #35bcb3 0%, #1e8a7f 100%)',
  mx: 'auto',
  mt: 1.5,
  mb: 2,
} as const;

const MOTION_FADE_UP_SX = {
  '@keyframes homeFadeUp': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'homeFadeUp 0.7s ease-out both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
} as const;

const BREADCRUMB_HOME = [{ name: 'Home', url: '/' }];

const Home: React.FC = () => {
  const happyPatientDisplay = useMemo(() => `${getHappyPatientCount()}+`, []);
  const { page } = useStaticPage('home');

  useSEOMeta({
    title: page?.metaTitle || page?.title || SEO_PAGES.home.title,
    description: page?.metaDescription || SEO_PAGES.home.description,
    keywords: page?.keywords || SEO_PAGES.home.keywords,
    canonicalUrl: page?.canonical || SEO_PAGES.home.canonicalUrl,
    ogImage: page?.ogImage || SEO_PAGES.home.ogImage,
    ogTitle: page?.ogTitle || page?.metaTitle || page?.title || SEO_PAGES.home.ogTitle,
    ogDescription: page?.ogDescription || page?.metaDescription || SEO_PAGES.home.ogDescription,
    ogUrl: SEO_PAGES.home.ogUrl,
    ogImageAlt: HOME_OG.imageAlt,
    ogImageWidth: HOME_OG.imageWidth,
    ogImageHeight: HOME_OG.imageHeight,
    robots: page?.robots,
    jsonLd: page?.jsonLd,
  });

  const [latestArticles, setLatestArticles] = useState<Article[]>([]);

  useEffect(() => {
    deferNonCriticalTask(() => {
      cachedRequest('homepage-featured-articles', async () => articleAPI.getAll({ limit: 20 }))
        .then((response: { data?: { articles?: Article[] } }) => {
          const articles: Article[] = response.data?.articles || [];
          const ordered = HOME_FEATURED_ARTICLE_SLUGS.map((slug) =>
            articles.find((article) => article.slug === slug || article._id === slug)
          ).filter((article): article is Article => Boolean(article));
          
          if (ordered.length < 3) {
            const remaining = articles.filter(
              (article) => !ordered.some((o) => o._id === article._id)
            );
            const combined = [...ordered, ...remaining].slice(0, 3);
            setLatestArticles(combined);
          } else {
            setLatestArticles(ordered.slice(0, 3));
          }
        })
        .catch((error) => {
          console.error('Failed to fetch articles:', error);
        });
    }, 3000);
  }, []);
  const features = [
    {
      title: 'Online Booking',
      description: 'Schedule online in minutes',
      link: '/appointments',
      buttonText: 'Book Now',
      icon: 'calendar',
      color: '#35bcb3',
    },
    {
      title: 'Our Services',
      description: 'Full list of treatments we offer',
      link: '/services',
      buttonText: 'Browse Services',
      icon: 'services',
      color: '#2aa899',
    },
    {
      title: 'Gallery',
      description: 'See our work',
      link: '/gallery',
      buttonText: 'View Gallery',
      icon: 'gallery',
      color: '#1e8a7f',
    },
    {
      title: 'Articles & Blog',
      description: 'Tips from our team',
      link: '/articles',
      buttonText: 'Read Articles',
      icon: 'articles',
      color: '#35bcb3',
    },
    {
      title: 'Get in Touch',
      description: 'Questions, directions, or emergencies',
      link: '/contact',
      buttonText: 'Contact Us',
      icon: 'contact',
      color: '#2aa899',
    },
    {
      title: 'About Us',
      description: 'Meet the people behind the clinic',
      link: '/about',
      buttonText: 'About Us',
      icon: 'about',
      color: '#1e8a7f',
    },
  ];

  const FEATURE_ICONS: Record<string, React.ReactElement> = {
    calendar: <CalendarTodayIcon />,
    services: <MedicalServicesIcon />,
    gallery: <PhotoLibraryIcon />,
    articles: <ArticleIcon />,
    contact: <ContactPhoneIcon />,
    about: <BusinessIcon />,
  };

  // Statistics — single source of social-proof numbers on the homepage
  const experienceYearsValue = useMemo(() => getExperienceYears(2021), []);
  const STAT_ICONS: Record<string, React.ReactElement> = {
    patients: <FaceIcon />,
    experience: <CalendarTodayIcon />,
    procedures: <MedicalServicesIcon />,
    rating: <StarIcon />,
  };

  const stats = [
    { value: happyPatientDisplay, label: 'Happy Patients', icon: 'patients' },
    { value: experienceYearsValue, label: 'Years Experience', icon: 'experience' },
    { value: '50+', label: 'Dental Procedures', icon: 'procedures' },
    { value: '5', label: 'Patient Rating', icon: 'rating' },
  ];

  const primaryServices = useMemo(() => [
    {
      title: 'Teeth Cleaning & Scaling',
      description: 'Keep your teeth clean, strong, and healthy with our routine care.',
      image: '/images/services/Teeth Cleaning and Scaling.png',
      link: '/services/teeth-cleaning-scaling/',
    },
    {
      title: 'Root Canal Treatment',
      description: 'Relieve pain and save your natural teeth with professional root canal therapy.',
      image: '/images/services/Root Canal Treatment.png',
      link: '/services/root-canal-treatment/',
    },
    {
      title: 'General & Preventive',
      description: 'Comprehensive checkups, cleanings, and preventive care for all ages.',
      image: '/images/services/General and Preventive.png',
      link: '/services/preventive-dentistry/',
    },
  ], []);


  // Before/After gallery preview
  const beforeAfterImages = [
    {
      before: '/images/beforeAfter/tramaBefore.webp',
      after: '/images/beforeAfter/tramaAfter.webp',
      title: 'Trauma Bridge',
      beforeTitle: 'Trauma Bridge Before',
      afterTitle: 'Trauma Bridge After',
      description:
        "Chipped a tooth or dealing with damage from an accident? Our trauma bridge treatments restore both function and appearance, so you're not stuck hiding your smile while you heal.",
    },
    {
      before: '/images/beforeAfter/whiteningBefore.webp',
      after: '/images/beforeAfter/whiteningAfter.webp',
      title: 'Teeth Whitening',
      beforeTitle: 'Teeth Whitening Before',
      afterTitle: 'Teeth Whitening After',
      description:
        "Coffee stains. Tea stains. Life stains, really; your teeth pick up all of it over time. Our whitening treatments handle that safely and quickly, and because a professional's overseeing the process, you're not stuck guessing as you would with a home kit.",
    },
    {
      before: '/images/beforeAfter/scalingBefore.webp',
      after: '/images/beforeAfter/scalingAfter.webp',
      title: 'Scaling',
      beforeTitle: 'Scaling Before',
      afterTitle: 'Scaling After',
      description:
        "Plaque's sneaky like that. It builds up quietly, and before you know it, you're dealing with something bigger: gum disease, cavities, the works. That's what our scaling and cleaning sessions are for: catching it early, before it gets the chance.",
    },
  ];

  const articleSchemaItems = useMemo(
    () => latestArticles.map((a) => ({ title: a.title, slug: a.slug || a._id || '' })),
    [latestArticles]
  );

  const serviceSchemaItems = useMemo(
    () => primaryServices.map((s) => ({ title: s.title })),
    [primaryServices]
  );

  const faqSchemaItems = useMemo(
    () => HOME_FAQ_ITEMS.map((f) => ({ question: f.question, answer: f.answer })),
    []
  );

  return (
    <>
      <HomePageSchema services={serviceSchemaItems} faqs={faqSchemaItems} articles={articleSchemaItems} />
      <BreadcrumbSchema items={BREADCRUMB_HOME} />
      {/* Premium Full-Width Screen Hero Banner with Cover Photo */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundImage: 'url(/images/herobg.png)',
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'right center', md: 'center' },
          pt: { xs: '76px', md: '88px' },
          pb: { xs: 8, md: 10 },
          mb: 6,
          boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item xs={12} md={8}>
              <Box sx={{ position: 'relative', zIndex: 1, ...MOTION_FADE_UP_SX }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'inline-block',
                    textTransform: 'uppercase',
                    color: '#35bcb3',
                    fontWeight: 850,
                    letterSpacing: '0.15em',
                    mb: 1.5,
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                  }}
                >
                  Where Beautiful Smiles Begin
                </Typography>

                <Typography
                  component="h1"
                  variant="h1"
                  sx={{
                    mb: 2,
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.2rem' },
                    fontWeight: 800,
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    color: 'white',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  Dental Clinic in Lalitpur | Dynamic Dental
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 1.5,
                    fontSize: { xs: '1.05rem', md: '1.2rem' },
                    color: 'rgba(255, 255, 255, 0.95)',
                    lineHeight: 1.6,
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  Dynamic Dental Clinic offers expert, gentle oral care in Kumaripati, Lalitpur. Get professional teeth cleaning, titanium implants, orthodontic braces, and painless root canals under strict WHO sterilization standards.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 3.5,
                    fontSize: '0.95rem',
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: 600,
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  Sunday – Friday: 10:00 AM – 6:00 PM | Call or WhatsApp: +977-984-0354608
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    component={Link}
                    to="/appointments"
                    variant="contained"
                    size="large"
                    startIcon={<CalendarTodayIcon />}
                    sx={{
                      backgroundColor: '#35bcb3',
                      color: 'white',
                      fontWeight: 700,
                      px: { xs: 3, md: 5 },
                      py: 1.8,
                      fontSize: '1.05rem',
                      borderRadius: '50px',
                      textTransform: 'none',
                      boxShadow: '0 4px 20px rgba(53, 188, 179, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#2aa899',
                        boxShadow: '0 6px 25px rgba(53, 188, 179, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Book Appointment
                  </Button>
                  <Button
                    component="a"
                    href="tel:+9779840354608"
                    onClick={() => trackPhoneClick('hero_banner')}
                    variant="outlined"
                    size="large"
                    startIcon={<PhoneIcon />}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      fontWeight: 700,
                      px: { xs: 3, md: 5 },
                      py: 1.8,
                      fontSize: '1.05rem',
                      borderRadius: '50px',
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        borderColor: 'white',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Call 984-0354608
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Box layout container for remaining page components */}
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>

          {/* Primary Services Grid Overview */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 3,
              mt: { xs: 4, md: 6 },
              mb: 6,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4, px: 2 }}>
              <Typography component="h2" variant="h2" sx={{ color: '#1e8a7f', fontWeight: 800 }}>
                Comprehensive Dental Services
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}>
                Professional dental care tailored to keep your smile healthy, bright, and confident.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {primaryServices.map((service) => (
                <Grid item xs={12} md={4} key={service.title}>
                  <Paper
                    component={Link}
                    to={service.link}
                    elevation={0}
                    sx={{
                      position: 'relative',
                      height: 280,
                      borderRadius: 4,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 3.5,
                      textDecoration: 'none',
                      background: `linear-gradient(rgba(30, 138, 127, 0.45) 0%, rgba(15, 78, 71, 0.85) 100%), url('${service.image.replace(/ /g, '%20')}') no-repeat center center`,
                      backgroundSize: 'cover',
                      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 15px 35px rgba(30, 138, 127, 0.25)',
                        background: `linear-gradient(rgba(30, 138, 127, 0.35) 0%, rgba(15, 78, 71, 0.75) 100%), url('${service.image.replace(/ /g, '%20')}') no-repeat center center`,
                        backgroundSize: 'cover',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                      <Typography component="h3" variant="h3" sx={{ color: '#ffffff', fontWeight: 800, mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2, lineHeight: 1.4 }}>
                        {service.description}
                      </Typography>
                      <Typography
                        variant="button"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          borderBottom: '1px solid rgba(255,255,255,0.6)',
                          pb: 0.25,
                          '&:hover': {
                            color: '#35bcb3',
                            borderBottomColor: '#35bcb3',
                          }
                        }}
                      >
                        Learn about {service.title} &rarr;
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Statistics — sole homepage social-proof numbers */}
          <Box
            component="section"
            sx={{
              mb: 6,
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 255, 254, 0.95) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(53, 188, 179, 0.15)',
              boxShadow: '0 12px 30px rgba(53, 188, 179, 0.06)',
              ...BELOW_FOLD_SX,
            }}
          >
            <Grid container spacing={2}>
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={stat.label}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: { xs: 1, sm: 2 },
                      px: 1,
                      borderRadius: 3,
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
                      borderRight: {
                        xs: index % 2 === 0 ? '1px solid rgba(53,188,179,0.15)' : 'none',
                        sm: index < stats.length - 1 ? '1px solid rgba(53,188,179,0.15)' : 'none',
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        backgroundColor: 'rgba(53, 188, 179, 0.04)',
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        '&:hover': { transform: 'none' },
                      },
                    }}
                  >
                    <Box sx={{ fontSize: '1.5rem', lineHeight: 1, mb: 1, display: 'flex', justifyContent: 'center', color: '#35bcb3' }} aria-hidden>
                      {STAT_ICONS[stat.icon]}
                    </Box>
                    <Typography component="p" sx={{ fontWeight: 800, color: '#1e8a7f', mb: 0.5, fontSize: { xs: '1.75rem', md: '2.5rem' }, m: 0 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>


          {/* Full Width Carousel */}
          <Box sx={{ width: '100%', my: 6 }}>
            <LazyMount minHeight={420} fallback={null}>
              <Carousel />
            </LazyMount>
          </Box>
        </Box>
      </Container>

      {/* Before & After Preview (Full Screen Width) */}
      <Box
        component="section"
        sx={{
          width: '100%',
          py: { xs: 8, md: 10 },
          my: 6,
          background: 'linear-gradient(160deg, #f7fffe 0%, #eef9f7 55%, #f5fbfa 100%)',
          borderTop: '1px solid rgba(53,188,179,0.12)',
          borderBottom: '1px solid rgba(53,188,179,0.12)',
          ...BELOW_FOLD_SX
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography component="h2" variant="h2">
              Before & After Transformations
            </Typography>
            <Box sx={SECTION_TITLE_ACCENT_SX} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 600, mx: 'auto' }}>
              Real transformations from our clinic. See how we help our patients smile with confidence.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {beforeAfterImages.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: '1px solid rgba(53, 188, 179, 0.12)',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 18px 40px rgba(53, 188, 179, 0.12)',
                      borderColor: 'rgba(53, 188, 179, 0.25)',
                    },
                    '&:hover img': {
                      transform: 'scale(1.03)',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      '&:hover': { transform: 'none' },
                      '&:hover img': { transform: 'none' },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', position: 'relative' }}>
                    <Box sx={{ flex: 1, position: 'relative' }}>
                      <PictureImage
                        src={item.before}
                        alt={item.beforeTitle}
                        title={item.beforeTitle}
                        width={400}
                        height={180}
                        sx={{ height: 180, filter: 'grayscale(20%)', transition: 'transform 0.35s ease' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          px: 1,
                          py: 0.3,
                          borderRadius: 1,
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                        }}
                      >
                        BEFORE
                      </Typography>
                      {/* Vertical separator line between Before and After halves */}
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '2px',
                          backgroundColor: '#ffffff',
                          opacity: 0.85,
                          zIndex: 2,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, position: 'relative' }}>
                      <PictureImage
                        src={item.after}
                        alt={item.afterTitle}
                        title={item.afterTitle}
                        width={400}
                        height={180}
                        sx={{ height: 180, transition: 'transform 0.35s ease' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          right: 4,
                          backgroundColor: '#35bcb3',
                          color: 'white',
                          px: 1,
                          py: 0.3,
                          borderRadius: 1,
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                        }}
                      >
                        AFTER
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2, textAlign: 'center', minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography
                      component="h3"
                      sx={{
                        color: '#1e8a7f',
                        m: 0,
                        fontWeight: 700,
                        fontSize: '1.0625rem',
                        lineHeight: 1.35,
                        letterSpacing: 'normal',
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              component={Link}
              to="/gallery"
              variant="outlined"
              sx={{
                borderColor: '#35bcb3',
                color: '#35bcb3',
                fontWeight: 600,
                px: 4,
                py: 1.2,
                borderRadius: '50px',
                '&:hover': {
                  borderColor: '#2aa899',
                  backgroundColor: 'rgba(53,188,179,0.05)',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              View Full Gallery →
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>

          {/* Our Services Grid */}
          <Box component="section" sx={{ mt: 6, mb: 8, ...BELOW_FOLD_SX }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography component="h2" variant="h2">
                Everything You Need for a Healthy Smile
              </Typography>
              <Box sx={SECTION_TITLE_ACCENT_SX} />
            </Box>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    component={Link}
                    to={feature.link}
                    elevation={2}
                    aria-label={`${feature.title} — ${feature.buttonText}`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      textDecoration: 'none',
                      color: 'inherit',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 18px 40px rgba(53, 188, 179, 0.12)',
                        borderColor: feature.color,
                      },
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid rgba(53, 188, 179, 0.12)',
                    }}
                  >
                    <Box
                      sx={{
                        height: '6px',
                        backgroundColor: feature.color,
                        width: '100%',
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <Avatar
                          sx={{
                            backgroundColor: feature.color,
                            color: 'white',
                            width: 44,
                            height: 44,
                            fontSize: '1.25rem',
                            flexShrink: 0,
                            boxShadow: `0 4px 10px ${feature.color}40`,
                          }}
                        >
                          {FEATURE_ICONS[feature.icon]}
                        </Avatar>
                        <Typography
                          component="h3"
                          sx={{
                            color: '#1a1a2e',
                            fontWeight: 700,
                            fontSize: '1.0625rem',
                            lineHeight: 1.35,
                            letterSpacing: 'normal',
                            m: 0,
                            pt: 0.5,
                          }}
                        >
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        size="medium"
                        variant="contained"
                        fullWidth
                        tabIndex={-1}
                        sx={{
                          background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                          color: '#ffffff',
                          borderRadius: '50px',
                          py: 1.2,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          pointerEvents: 'none',
                          boxShadow: `0 4px 15px ${feature.color}40`,
                        }}
                      >
                        {feature.buttonText} →
                      </Button>
                    </CardActions>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

        </Box>
      </Container>

      <LazyMount minHeight={280} rootMargin="200px">
        <Suspense fallback={null}>
          <WhyChooseUsSection sx={BELOW_FOLD_SX} />
        </Suspense>
      </LazyMount>

      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>

          {/* Latest Articles Section */}
          {latestArticles.length > 0 && (
            <Box component="section" sx={{ mt: 6, mb: 6 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>

                <Typography component="h2" variant="h2">
                  Dental Health Tips & Articles
                </Typography>
                <Box sx={SECTION_TITLE_ACCENT_SX} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 'none', mx: 'auto' }}>
                  Stay informed with the latest dental health advice from our team. Browse{' '}
                  <MuiLink component={Link} to="/articles" sx={{ color: '#1e8a7f', fontWeight: 600 }}>
                    all articles
                  </MuiLink>
                  .
                </Typography>
              </Box>
              <Grid container spacing={3} alignItems="stretch">
                {latestArticles.map((article) => (
                  <Grid item xs={12} sm={6} md={4} key={article._id || ''} sx={{ display: 'flex' }}>
                    <Paper
                      elevation={2}
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 4,
                        border: '1px solid rgba(53, 188, 179, 0.12)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 18px 40px rgba(53, 188, 179, 0.12)',
                          borderColor: '#35bcb3',
                        },
                      }}
                    >
                      <Box sx={{ height: '6px', backgroundColor: '#35bcb3', width: '100%', flexShrink: 0 }} />
                      <ArticleImage imageUrl={article.imageUrl} alt={article.title} variant="card" />
                      <Box
                        sx={{
                          p: 3,
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                          minHeight: 0,
                        }}
                      >
                        <Chip
                          label={article.category}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            backgroundColor: '#35bcb3',
                            color: 'white',
                            fontWeight: 600,
                            mb: 2,
                          }}
                        />
                        <Typography
                          component="h3"
                          sx={{
                            mb: 1.5,
                            fontWeight: 700,
                            fontSize: '1.0625rem',
                            lineHeight: 1.4,
                            letterSpacing: 'normal',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '4.2em',
                          }}
                        >
                          {article.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            flexGrow: 1,
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '4.8em',
                          }}
                        >
                          {article.excerpt}
                        </Typography>
                        <Button
                          component={Link}
                          to={`/articles/${article.slug || article._id}/`}
                          variant="contained"
                          fullWidth
                          sx={{
                            mt: 'auto',
                            backgroundColor: '#35bcb3',
                            borderRadius: '50px',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#2aa899',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          Read Article
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  component={Link}
                  to="/articles"
                  variant="outlined"
                  sx={{
                    borderColor: '#35bcb3',
                    color: '#35bcb3',
                    fontWeight: 600,
                    px: 4,
                    py: 1.2,
                    borderRadius: '50px',
                    '&:hover': {
                      borderColor: '#2aa899',
                      backgroundColor: 'rgba(53,188,179,0.05)',
                    }
                  }}
                >
                  View All Articles →
                </Button>
              </Box>
            </Box>
          )}

          {/* Newsletter Subscription */}
          <Box component="section" sx={{ mt: 6, mb: 6, ...SECTION_SOFT_SX }}>
            <LazyMount minHeight={160}>
              <NewsletterSubscribe source="homepage" />
            </LazyMount>
          </Box>

          {/* Google Reviews Section */}
          <Box component="section" sx={{ mt: 6, mb: 6 }}>
            <LazyMount minHeight={180} rootMargin="80px">
              <GoogleReviews />
            </LazyMount>
          </Box>

          <LazyMount minHeight={320} rootMargin="400px">
            <Suspense fallback={null}>
              <GoogleMapEmbed
                title="Contact Dynamic Dental Clinic"
                subtitle="Have a question or ready to book? Visit us in Kumaripati, Lalitpur, or call us directly. We're open Sunday – Friday."
              />
            </Suspense>
          </LazyMount>

          <LazyMount minHeight={200} rootMargin="400px">
            <Suspense fallback={null}>
              <HomeFaqSection />
            </Suspense>
          </LazyMount>

          {/* Call to Action */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #35bcb3 0%, #1e8a7f 100%)',
              color: 'white',
              borderRadius: 4,
              py: { xs: 5, md: 7 },
              px: { xs: 3, md: 6 },
              mb: 4,
              textAlign: 'center',
              boxShadow: '0 15px 35px rgba(30, 138, 127, 0.25)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(53,188,179,0.2) 0%, transparent 50%)',
                pointerEvents: 'none',
              }
            }}
          >
            <Typography component="h2" variant="h2" sx={{ position: 'relative', zIndex: 1, mb: 2 }}>
              Ready to Transform Your Smile?
            </Typography>
            <Typography variant="body1" component="p" sx={{ opacity: 0.95, mb: 4, maxWidth: '720px', mx: 'auto', position: 'relative', zIndex: 1 }}>
              You don&apos;t need to live with a smile you&apos;re not confident about. Book a visit with Dynamic Dental Clinic and let&apos;s figure out the right treatment for you—no pressure, just honest advice from a dental clinic in Lalitpur that patients trust.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} justifyContent="center" sx={{ gap: 2, position: 'relative', zIndex: 1 }}>
              <Button
                component={Link}
                to="/appointments"
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                  color: '#1e8a7f',
                  fontWeight: 700,
                  px: 6,
                  py: 1.8,
                  fontSize: '1.1rem',
                  borderRadius: '50px',
                  boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ffffff 0%, #e8e8e8 100%)',
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 8px 25px rgba(255,255,255,0.4)',
                  },
                  '&::after': {
                    content: '" →"',
                    transition: 'margin-left 0.3s ease',
                  },
                  '&:hover::after': {
                    marginLeft: '4px',
                  }
                }}
              >
                Schedule Your Visit
              </Button>
              <Button
                component="a"
                href="tel:9840354608"
                variant="outlined"
                size="large"
                startIcon={<PhoneIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)',
                  borderWidth: 2,
                  color: 'white',
                  fontWeight: 700,
                  px: 5,
                  py: 1.8,
                  fontSize: '1.1rem',
                  borderRadius: '50px',
                  textTransform: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: 'white',
                    borderWidth: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 25px rgba(255,255,255,0.2)',
                  }
                }}
              >
                Call Clinic: 9840354608
              </Button>
            </Stack>
          </Box>

          {/* Factual Entity Summary for search crawlers and AI answer engines */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              border: '1px solid rgba(53, 188, 179, 0.15)',
              backgroundColor: 'white',
              mb: 4,
            }}
          >
            <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', mb: 2 }}>
              Dynamic Dental Clinic Factual Entity Summary & Clinical Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              Dynamic Dental Clinic is a premier family-friendly and restorative dental clinic located in Kumaripati, Lalitpur, Nepal (situated above Global IME Bank and beside Yamaha Showroom). The clinic provides comprehensive, patient-centered oral care using modern rotary equipment and WHO-standard sterilization protocols.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" component="p" sx={{ fontWeight: 800, color: '#1e8a7f', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Registered Practitioners
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Our clinic is directed by Nepal Medical Council (NMC) licensed practitioners:
                  <br />
                  &bull; <strong>Dr. Preksha Shah</strong> (Lead Dentist, NMC Registered)
                  <br />
                  &bull; <strong>Dr. Survesh Marhatta</strong> (Senior Implantologist, NMC Registered)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" component="p" sx={{ fontWeight: 800, color: '#1e8a7f', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Core Treatments
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Our treatment catalog includes: teeth cleaning and scaling, titanium dental implants, clear braces and ceramic orthodontic alignments, rotary root canals, professional whitening, crowns, bridges, and dental veneers.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" component="p" sx={{ fontWeight: 800, color: '#1e8a7f', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Clinic Operations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  <strong>Address:</strong> Kumaripati, Lalitpur, Nepal
                  <br />
                  <strong>Hours:</strong> Sunday – Friday, 10:00 AM – 6:00 PM (closed Saturday)
                  <br />
                  <strong>Hotline:</strong> 984-0354608 (same-day emergency dental slots available)
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default Home;
